import { posix as pathPosix } from 'node:path';
import type { GlobFilesArgs, GlobFilesResult } from '../../session';

const VCS_DIRECTORIES_TO_EXCLUDE = [
  '.git',
  '.svn',
  '.hg',
  '.bzr',
  '.jj',
  '.sl',
] as const;

export const DEFAULT_GLOB_LIMIT = 100;

export type ResolvedGlobSearch = {
  searchPath: string;
  searchPattern: string;
};

export function globSearchPathCandidate(
  args: GlobFilesArgs,
): string | undefined {
  if (pathPosix.isAbsolute(args.pattern)) {
    const { baseDir } = extractGlobBaseDirectory(args.pattern);
    return baseDir || args.path;
  }
  if (args.path) {
    return args.path;
  }

  const { baseDir } = extractGlobBaseDirectory(args.pattern);
  return baseDir && baseDir !== '.' ? baseDir : undefined;
}

export function resolveGlobSearch(
  args: GlobFilesArgs,
  resolvePath: (path?: string) => string,
): ResolvedGlobSearch {
  let searchPathArg = args.path;
  let searchPattern = args.pattern;

  if (pathPosix.isAbsolute(args.pattern)) {
    const { baseDir, relativePattern } = extractGlobBaseDirectory(args.pattern);
    if (baseDir) {
      searchPathArg = baseDir;
      searchPattern = relativePattern;
    }
  }

  return {
    searchPath: resolvePath(searchPathArg),
    searchPattern,
  };
}

export function buildRipgrepGlobArgs(searchPattern: string): string[] {
  const result = [
    '--files',
    '--glob',
    searchPattern,
    '--sort=modified',
    '--no-ignore',
    '--hidden',
  ];

  for (const dir of VCS_DIRECTORIES_TO_EXCLUDE) {
    result.push(
      '--glob',
      `!${dir}`,
      '--glob',
      `!${dir}/**`,
      '--glob',
      `!**/${dir}`,
      '--glob',
      `!**/${dir}/**`,
    );
  }

  return result;
}

export function createGlobFilesResult(
  args: GlobFilesArgs,
  paths: string[],
  displayPath: (path: string) => string,
  startedAt: number,
): GlobFilesResult {
  const limit = normalizeGlobLimit(args.limit);
  const filenames = paths.slice(0, limit).map(displayPath);
  return {
    durationMs: Date.now() - startedAt,
    numFiles: filenames.length,
    filenames,
    truncated: paths.length > limit,
  };
}

function extractGlobBaseDirectory(pattern: string): {
  baseDir: string;
  relativePattern: string;
} {
  const match = /[*?[{]/u.exec(pattern);
  if (!match || match.index === undefined) {
    return {
      baseDir: pathPosix.dirname(pattern),
      relativePattern: pathPosix.basename(pattern),
    };
  }

  const staticPrefix = pattern.slice(0, match.index);
  const lastSeparatorIndex = staticPrefix.lastIndexOf('/');
  if (lastSeparatorIndex === -1) {
    return {
      baseDir: '',
      relativePattern: pattern,
    };
  }

  const baseDir =
    lastSeparatorIndex === 0 ? '/' : staticPrefix.slice(0, lastSeparatorIndex);
  return {
    baseDir,
    relativePattern: pattern.slice(lastSeparatorIndex + 1),
  };
}

function normalizeGlobLimit(limit: number | undefined): number {
  if (limit === undefined || !Number.isFinite(limit)) {
    return DEFAULT_GLOB_LIMIT;
  }
  return Math.max(1, Math.floor(limit));
}
